import createSelector from 'selectorator'
import { path, map, identity, filter, isEmpty, pipe, values, pathOr } from 'ramda'
import { propertyApiSelectors } from '../../api/property'
import { portfolioApiSelectors } from '../../api/portfolio'
import { $TSFixMe } from 'types/ts-migrate'
import { partyApiSelectors } from 'modules/api/party'

export const getPropertyFilters = createSelector([identity], state => {
  const propertySummaries = propertyApiSelectors.getSummaries(state)
  const portfolioSummaries = portfolioApiSelectors.getPortfolioSummaries(state)

  // `values` first, then `map` — equivalent to mapping the record and taking its values.
  const properties = pipe(
    values,
    map((summary: any) => ({
      propertyId: path(['propertyId'], summary),
      portfolioId: path(['portfolioId'], summary),
      mainText: path(['mainText'], summary),
      secondaryText: path(['secondaryText'], summary),
      endDate: path(['endDate'], summary),
      tags: path(['tag'], summary),

      activeLeases: filter((p: any) => {
        return (
          p.propertyId === path(['propertyId'], summary) &&
          (portfolioApiSelectors.isActivePortfolio(state)(p.portfolioId, true) ||
            portfolioApiSelectors.isPortfolioExpiring(state)(p.portfolioId, true))
        )
      }, portfolioSummaries),
    })),
  )(propertySummaries)

  const occupiedProperties = filter((p: any) => !isEmpty(p.activeLeases), properties)
  const vacantProperties = filter((p: any) => isEmpty(p.activeLeases), properties)

  return [
    {
      key: 'all',
      label: 'All',
      count: properties.length,
      properties: properties,
    },
    {
      key: 'occupied',
      label: 'Occupied',
      count: occupiedProperties.length,
      properties: occupiedProperties,
    },
    {
      key: 'vacant',
      label: 'Vacant',
      count: vacantProperties.length,
      properties: vacantProperties,
    },
  ]
})

export const getPropertyViewData = createSelector([identity], state => (id: any) => {
  const property = propertyApiSelectors.getSummaryById(state)(id)
  const ownerId = property?.ownerId
  const { bank, accountNumber } = partyApiSelectors.getPartyBankDetails(state)(ownerId)
  const idNumber =
    partyApiSelectors.getPartyTypeById(state)(ownerId) === 'person'
      ? partyApiSelectors.getPersonsIdNumber(state)(ownerId)
      : false

  return {
    title: pathOr('Loading...', ['mainText'], property),
    subTitle: pathOr('...', ['secondaryText'], property),
    propertyId: property?.propertyId,
    portfolioId: property?.portfolioId,
    owner: {
      name: partyApiSelectors.getPartyNameById(state)(ownerId) || '...',
      email: partyApiSelectors.getPartyEmailById(state)(ownerId) || '...',
      contactNumber: partyApiSelectors.getPartyTelNumberById(state)(ownerId) || '...',
      idNumber: idNumber || '...',
      bank: bank || '...',
      accountNumber: accountNumber || '...',
      ownerId,
    },
    tenantId: property?.tenantId,
  }
})

export const getInitialValuesForEdit = createSelector([identity], state => (id: any) => ({
  propertyDescription: portfolioApiSelectors.getPortfolioMetaData(state)(id),
  lightstoneId: propertyApiSelectors.getPropertyLightstoneId(state)(id),
  placeId: propertyApiSelectors.getPropertyPlaceId(state)(id),
  buildingName: propertyApiSelectors.getBuildingNameById(state)(id),
  unitNumber: propertyApiSelectors.getUnitNumberById(state)(id),
  schemeNumber: propertyApiSelectors.getSchemeNumberById(state)(id),
  schemeName: propertyApiSelectors.getSchemeNameById(state)(id),
  streetAddress: propertyApiSelectors.getPropertyStreetAddress(state)(id),
  city: propertyApiSelectors.getPropertyCity(state)(id),
  province: propertyApiSelectors.getPropertyProvince(state)(id),
  postalCode: propertyApiSelectors.getPropertyPostalCode(state)(id),
  country: propertyApiSelectors.getPropertyCountry(state)(id),
}))
